import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import '../components/aiinterview/aiinterview.css';
import { aiCandidateAPI } from '../lib/aiInterviewApi';
import { useMediaStream } from '../components/aiinterview/media';
import { CandidateShell } from '../components/aiinterview/candidate/CandidateShell';
import ConsentStep from '../components/aiinterview/candidate/ConsentStep';
import AccommodationStep from '../components/aiinterview/candidate/AccommodationStep';
import TechCheckStep from '../components/aiinterview/candidate/TechCheckStep';
import IdentityStep from '../components/aiinterview/candidate/IdentityStep';
import InterviewRoom from '../components/aiinterview/room/InterviewRoom';
import { MessageScreen, ThankYouScreen } from '../components/aiinterview/candidate/CandidateEndScreens';

const DONE = ['completed', 'scoring', 'report_ready', 'score_error'];

function initialStep(d) {
  if (['invited', 'opened'].includes(d.status)) return 'consent';
  if (['consented', 'starting'].includes(d.status)) return 'accommodations';
  if (d.status === 'in_progress') return 'room';
  if (d.status === 'interrupted') return 'interrupted';
  if (DONE.includes(d.status)) return 'done';
  if (d.status === 'declined') return 'declined';
  return 'closed';
}

export default function AIInterviewCandidatePage() {
  const { token } = useParams();
  const [search] = useSearchParams();
  const media = useMediaStream();
  const [ctx, setCtx] = useState(null);
  const [step, setStep] = useState('loading');

  const load = useCallback(async () => {
    const { data } = await aiCandidateAPI.context(token);
    setCtx(data);
    return data;
  }, [token]);

  useEffect(() => {
    load().then((d) => setStep(initialStep(d))).catch((e) => setStep(e.response?.status === 404 ? 'not_found' : 'network'));
  }, [load]);

  const steps = useMemo(() => (ctx ? ['consent', 'accommodations', 'tech', ...(ctx.identity_required ? ['identity'] : []), 'room'] : null), [ctx]);
  const next = (from) => {
    let n = steps[steps.indexOf(from) + 1];
    if (n === 'identity' && ctx.identity_done) n = 'room';
    setStep(n);
  };
  const finished = useCallback(() => {
    media.stop();
    load().catch(() => {}).finally(() => setStep('done'));
  }, [media, load]);

  if (step === 'room') {
    return <InterviewRoom ctx={ctx} token={token} media={media} onFinished={finished} onInterrupted={() => setStep('interrupted')} />;
  }

  let body = null;
  if (step === 'loading') body = <div className="iv-card" data-testid="candidate-loading"><p className="iv-sub">Loading your interview…</p></div>;
  else if (step === 'not_found') body = <MessageScreen testId="candidate-not-found" title="This interview link isn't valid" body="The link may be mistyped, or the interview data was deleted. Please check the invitation email or contact the recruiter." />;
  else if (step === 'network') body = <MessageScreen testId="candidate-network-error" title="We couldn't load your interview" body="Please check your connection and refresh the page." />;
  else if (step === 'consent') body = <ConsentStep ctx={ctx} token={token} onDone={() => load().then(() => next('consent'))} onDeclined={() => load().then(() => setStep('declined'))} />;
  else if (step === 'accommodations') body = <AccommodationStep ctx={ctx} token={token} onDone={() => next('accommodations')} onRefresh={load} />;
  else if (step === 'tech') body = <TechCheckStep ctx={ctx} token={token} media={media} onDone={() => next('tech')} />;
  else if (step === 'identity') body = <IdentityStep token={token} media={media} onDone={() => load().then(() => setStep('room'))} />;
  else if (step === 'done') body = <ThankYouScreen ctx={ctx} token={token} explain={search.get('explain') === '1'} />;
  else if (step === 'declined') body = <MessageScreen testId="candidate-declined-screen" title="You've declined the AI interview" body="We've let the hiring team know you'd like an interview with a person. They'll contact you directly. Declining doesn't count against you." />;
  else if (step === 'interrupted') body = <MessageScreen testId="candidate-interrupted-screen" title="Your interview was interrupted" body="The connection was lost for more than a few minutes. The hiring team has been notified and can reopen your interview so you can continue from your last answer. Look out for an email from them." />;
  else body = <MessageScreen testId="candidate-closed-screen" title="This interview is no longer available" body={`The invitation has ${ctx?.status === 'revoked' ? 'been withdrawn' : 'expired'}. If you still want to interview, please contact the recruiter.`} />;

  return <CandidateShell ctx={ctx} steps={steps} current={step}>{body}</CandidateShell>;
}
